import type {
  RouteLocation,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import type { Acl, User } from "vue-simple-acl/types/acl";
import { BREADCRUMBS } from "~/core/constants/breadcrumbs";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  PATH_USER_ACCOUNT,
  PATH_USER_ACCOUNT_ADMINISTRATION,
  PATH_USER_ACCOUNT_ADMINISTRATION_LANGUAGE,
  PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
  PATH_USER_ACCOUNT_LANGUAGE,
  PATH_USER_ACCOUNT_PROFILE,
  PATH_USER_MANAGEMENT,
  PATH_USER_MANAGEMENT_GROUPS,
  PATH_USER_MANAGEMENT_GROUPS_CODE,
  PATH_USER_MANAGEMENT_PROFILES,
  PATH_USER_MANAGEMENT_PROFILES_ID,
  PATH_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
  PATH_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
  PATH_USER_MANAGEMENT_USERS,
  PATH_USER_MANAGEMENT_USERS_LOGIN,
} from "~/modules/user/constants";
import { router } from "~/plugins/router";

const routes: RouteRecordRaw[] = [
  // User account pages
  {
    // The user management page does not exist by itself, it is a wrapper for the profile and language pages
    // accessible via the usage of tabs. The navigation to this page (via the left panel or the URL) must redirect
    // to the user tab.
    path: PATH_USER_ACCOUNT,
    redirect: PATH_USER_ACCOUNT_PROFILE,
  },
  {
    path: PATH_USER_ACCOUNT_PROFILE,
    component: () => import("~/modules/user/views/UserAccountPage.vue"),
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
    },
  },
  {
    path: PATH_USER_ACCOUNT_LANGUAGE,
    component: () => import("~/modules/user/views/UserAccountPage.vue"),
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
    },
  },
  {
    path: PATH_USER_ACCOUNT_ADMINISTRATION,
    redirect: PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
  },
  {
    path: PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
    component: () => import("~/modules/user/views/UserAccountPage.vue"),
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
      layout: "AdministrationLayout",
    },
  },
  {
    path: PATH_USER_ACCOUNT_ADMINISTRATION_LANGUAGE,
    component: () => import("~/modules/user/views/UserAccountPage.vue"),
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
      layout: "AdministrationLayout",
    },
  },
  // User management pages
  {
    // The user management page does not exist by itself, it is a wrapper for the user, group and profile pages
    // accessible via the usage of tabs. The navigation to this page (via the left panel or the URL) must redirect
    // to the user tab.
    path: PATH_USER_MANAGEMENT,
    redirect: PATH_USER_MANAGEMENT_USERS,
  },
  {
    path: PATH_USER_MANAGEMENT_USERS,
    component: () => import("~/modules/user/views/UserManagementPage.vue"),
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
      breadcrumb: BREADCRUMBS.USER_MANAGEMENT,
    },
  },
  {
    path: PATH_USER_MANAGEMENT_USERS_LOGIN,
    component: () => import("~/modules/user/views/UserManagementPage.vue"),
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
      breadcrumb: BREADCRUMBS.USER_MANAGEMENT,
    },
  },
  // user group management page
  {
    path: PATH_USER_MANAGEMENT_GROUPS,
    component: () => import("~/modules/user/views/UserManagementPage.vue"),
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
      breadcrumb: BREADCRUMBS.GROUP,
    },
  },
  {
    path: PATH_USER_MANAGEMENT_GROUPS_CODE,
    component: () => import("~/modules/user/views/UserManagementPage.vue"),
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_WRITE_USER),
      breadcrumb: BREADCRUMBS.GROUP,
    },
  },
  {
    path: PATH_USER_MANAGEMENT_PROFILES,
    component: () => import("~/modules/user/views/UserManagementPage.vue"),
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_USER_MANAGEMENT_PROFILES_ID,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
        params: { profileId: to.params.profileId },
      }),
  },
  {
    path: PATH_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
    component: () => import("~/modules/user/views/UserManagementPage.vue"),
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
    component: () => import("~/modules/user/views/UserManagementPage.vue"),
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
];

export default routes;
